<template>
  <div class="stat">
    <span class="stat__label">{{ label }}</span>
    <UiSkeleton v-if="loading" width="44px" height="26px" />
    <span v-else class="stat__value" :class="tone ? `stat__value--${tone}` : ''">{{ value }}</span>
  </div>
</template>

<script setup>
import UiSkeleton from './UiSkeleton.vue'

// A stat tile: one figure with its name. The building block of the summary row
// every list screen opens with.
//
// It sizes ITSELF (grow a little, cap at 15rem) so the row that holds it only
// needs `display: flex; flex-wrap: wrap`. The cap is the point: these used to
// be cards stretched across the whole content width, which on a wide screen
// meant three enormous boxes with a digit floating in each — the number read
// as lost, not as a summary.
defineProps({
  label: { type: String, required: true },
  value: { type: [String, Number], default: '' },
  // Colors the figure. For when the number carries a state worth signalling
  // (money owed, days left open) — not for decorating every tile.
  tone: { type: String, default: '' },   // primary | success | warning | danger
  loading: Boolean,
})
</script>

<style scoped>
.stat {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  flex: 1 1 9rem;
  max-width: 15rem;
  min-width: 8rem;
  padding: var(--space-4) var(--space-5);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  /* La cifra se mide contra su PROPIA tarjeta (ver .stat__value). */
  container-type: inline-size;
}

.stat__label {
  font-size: var(--text-xs);
  color: var(--color-text-soft);
}

/* Proportional figures on purpose: tabular-nums gives every digit the width of
   a zero, and a lone "121" at this size reads loose. Columns that must align
   vertically are the place for tabular figures — this is not a column. */
/* El tamaño lo pone el ancho de la tarjeta y no la ventana. Con uno fijo, un
   importe de obra —«1.556.077,30 €»— se salía de la tarjeta en un teléfono
   (dos por fila, 113 px por dentro) y en el panel lateral de escritorio (tres
   por fila). Catorce caracteres caben en un 14 % del ancho por carácter, y el
   techo es el tamaño de siempre: en una tarjeta ancha no cambia nada. */
.stat__value {
  font-size: clamp(var(--text-md), 14cqi, var(--text-2xl));
  font-weight: 650;
  line-height: 1.1;
  color: var(--color-text);
}
.stat__value--primary { color: var(--color-primary); }
.stat__value--success { color: var(--color-success); }
.stat__value--warning { color: var(--color-warning); }
.stat__value--danger { color: var(--color-danger); }

/* On a phone the tiles pair up instead of stacking one per row. */
@media (max-width: 640px) {
  .stat {
    flex-basis: calc(50% - var(--space-3)); min-width: 0; max-width: none;
    padding: var(--space-3) var(--space-4);
  }
}
</style>
